// Deeplink Landing Page — helper utilities
import {
  PLATFORMS,
  PUBLIC_DESTINATIONS,
  STORE_URLS,
  APP_SCHEME,
} from "./constants";

/**
 * Detect client platform from the User-Agent string.
 * Reference: Section 7.4 — Step 1
 */
export const detectPlatform = (userAgent = navigator.userAgent || "") => {
  const ua = userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return PLATFORMS.IOS;
  // iPad on iOS 13+ reports as Mac — additional touch check
  if (/macintosh/.test(ua) && navigator.maxTouchPoints > 1) {
    return PLATFORMS.IOS;
  }
  if (/android/.test(ua)) return PLATFORMS.ANDROID;
  return PLATFORMS.DESKTOP;
};

/**
 * Get the base URL for the deeplink microservice.
 * Reads from REACT_APP_API_URL and appends /deeplink/v1.
 */
export const getDeeplinkApiBase = () => {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return `${base}/deeplink/v1`;
};

// Allowlist of supported deeplink query keys. Anything else from the URL is
// dropped so that user-controlled keys cannot reach a property assignment —
// this is the recommended mitigation for CodeQL `js/remote-property-injection`.
// Keep this in sync with consumers in index.js / ScreenDesktopPublic.js.
const ALLOWED_QUERY_KEYS = ["destination", "user_state", "lang", "ref_id"];

/**
 * Parse the query string from window.location.search into a plain object.
 *
 * Only keys in ALLOWED_QUERY_KEYS are copied through. The result has a null
 * prototype so even if the allowlist is later widened, prototype pollution
 * via keys like `__proto__` cannot occur.
 */
export const parseQuery = (search = window.location.search) => {
  const out = Object.create(null);
  if (!search || search.length <= 1) return out;
  const params = new URLSearchParams(search);
  for (let i = 0; i < ALLOWED_QUERY_KEYS.length; i += 1) {
    const key = ALLOWED_QUERY_KEYS[i];
    const value = params.get(key);
    if (value !== null) {
      out[key] = value;
    }
  }
  return out;
};

/**
 * Extract the destination identifier from either:
 *  - the query string `?destination=open_mf` (preferred)
 *  - the path segment `/deeplink/landing/open_mf` (fallback)
 */
export const extractDestination = (
  search = window.location.search,
  pathname = window.location.pathname
) => {
  const q = parseQuery(search);
  if (q.destination) return q.destination;
  const segments = pathname.split("/").filter(Boolean);
  // Path: /deeplink/landing/<destination>
  if (segments[0] === "deeplink" && segments[1] === "landing" && segments[2]) {
    return segments[2];
  }
  return null;
};

/**
 * Determine if a destination is public (Fund Tab/Info/Article).
 * Public destinations get Screen B (with web fallback).
 * Private destinations get Screen C (download only).
 */
export const isPublicDestination = destination =>
  PUBLIC_DESTINATIONS.includes(destination);

/**
 * Build the app scheme URL for a destination.
 * Examples:
 *   open_mf       -> cpamapp://open-mf
 *   fund_info     -> cpamapp://fund-info
 *   fund/MABALANCED-D (raw)  -> passthrough
 */
export const buildAppSchemeUrl = (destination, refId, lang) => {
  // destinations use snake_case in the API but kebab-case in the scheme
  // e.g. open_mf -> open-mf, soft_onboard -> soft-onboard
  const kebab = destination.replace(/_/g, "-");
  const url = new URL(`${APP_SCHEME}${kebab}`);
  if (refId) url.searchParams.set("ref", refId);
  if (lang) url.searchParams.set("lang", lang);
  return url.toString();
};

/**
 * Get the store URL for the user's platform.
 */
export const getStoreUrl = platform => {
  if (platform === PLATFORMS.IOS) return STORE_URLS.IOS;
  if (platform === PLATFORMS.ANDROID) return STORE_URLS.ANDROID;
  return STORE_URLS.IOS; // sensible default for desktop fallback
};

/**
 * Trigger the cpamapp:// URL scheme to attempt opening the app.
 * Uses window.location to allow the OS to intercept.
 */
export const triggerAppScheme = url => {
  // Use location.assign so the browser tries to navigate to the scheme
  window.location.assign(url);
};
